// frontend/src/pages/MatchingExercise.jsx
// Exercice de matching de vocabulaire

import React, { useState, useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getArticleDefini } from '../utils/exerciseUtils';
import { useLangueEtudiee } from '../hooks/useLangueEtudiee';
import './ExerciseShared.css';
import './MatchingExercise.css';

const MAX_VISIBLE = 10;

function MatchingExercise() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const langueEtudiee = useLangueEtudiee();
  const isEmbed = searchParams.get('embed') === 'true';
  const [step, setStep] = useState(1);
  const savedParams = JSON.parse(localStorage.getItem('matchingParams') || '{}');
  // langueEtudiee (param WP) prioritaire sur la préférence sauvegardée
  const [params, setParams] = useState({
    langueSource: savedParams.langueSource || 'fr',
    langueCible: langueEtudiee || savedParams.langueCible || 'en',
    typeMot: savedParams.typeMot || 'all',
    nombreQuestions: savedParams.nombreQuestions || 10,
  });
  // Paires visibles à l'écran (max 10)
  const [motsSource, setMotsSource] = useState([]);
  const [motsCible, setMotsCible] = useState([]);
  // Pool de paires en attente
  const [poolSource, setPoolSource] = useState([]);
  const [poolCible, setPoolCible] = useState([]);
  // Total de questions et compteur de paires résolues
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [resolved, setResolved] = useState(0);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Liste des langues disponibles
  const langues = [
    { code: 'fr', name: t('langues.fr'), flag: '🇫🇷' },
    { code: 'en', name: t('langues.en'), flag: '🇬🇧' },
    { code: 'es', name: t('langues.es'), flag: '🇪🇸' },
  ];

  // Liste des types de mots
  const typesMots = [
    { value: 'all', label: t('matching.typeAll') },
    { value: 'nom', label: t('matching.typeNom') },
    { value: 'adjectif', label: t('matching.typeAdjectif') },
    { value: 'adverbe', label: t('matching.typeAdverbe') },
    { value: 'pronom', label: t('matching.typePronom') },
    { value: 'adjectif_indefini', label: t('matching.typeAdjectifIndefini') },
  ];

  // Sauvegarder les paramètres dans localStorage
  useEffect(() => {
    localStorage.setItem('matchingParams', JSON.stringify(params));
  }, [params]);

  // Fisher-Yates shuffle
  const shuffle = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const startExercise = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Démarrage exercice avec paramètres:', params);
      // Demander toutes les paires au backend (quantite = nombreQuestions)
      const queryParams = {
        langueSource: params.langueSource,
        langueCible: params.langueCible,
        typeMot: params.typeMot,
        quantite: params.nombreQuestions,
      };
      const response = await fetch(`/api/exercices/vocabulaire/matching?${new URLSearchParams(queryParams)}`);
      const data = await response.json();

      console.log('📥 Réponse brute backend:', JSON.stringify(data, null, 2));

      if (!data.success) {
        throw new Error(data.error || 'Erreur lors de la récupération des mots');
      }

      if (data.data.motsSource.length === 0) {
        throw new Error('Aucun mot source retourné par le backend');
      }

      const allSource = data.data.motsSource;
      const allCible = data.data.motsCible;
      const total = allSource.length;

      // Séparer : les 10 premières paires sont visibles, le reste en pool
      const visibleSource = allSource.slice(0, MAX_VISIBLE);
      const remainingSource = allSource.slice(MAX_VISIBLE);

      // Pour les mots cible, on doit extraire ceux qui correspondent aux paires visibles
      const visiblePairIds = new Set(visibleSource.map(m => m.pairId));
      const visibleCible = [];
      const remainingCible = [];
      for (const mot of allCible) {
        if (visiblePairIds.has(mot.pairId)) {
          visibleCible.push(mot);
        } else {
          remainingCible.push(mot);
        }
      }

      setMotsSource(visibleSource);
      setMotsCible(shuffle(visibleCible));
      setPoolSource(remainingSource);
      setPoolCible(remainingCible);
      setTotalQuestions(total);
      setResolved(0);
      setScore(0);
      setStep(2);
    } catch (err) {
      setError(err.message);
      console.error("❌ Erreur lors du chargement de l'exercice:", err);
    } finally {
      setLoading(false);
    }
  };

  // Ajouter une paire du pool vers l'affichage
  const pullFromPool = (currentSource, currentCible, currentPoolSource, currentPoolCible) => {
    if (currentPoolSource.length === 0) {
      return { newSource: currentSource, newCible: currentCible, newPoolSource: currentPoolSource, newPoolCible: currentPoolCible };
    }

    // Prendre la première paire du pool
    const newMot = currentPoolSource[0];
    const newPoolSrc = currentPoolSource.slice(1);

    // Trouver le mot cible correspondant dans le pool
    const cibleIndex = currentPoolCible.findIndex(m => m.pairId === newMot.pairId);
    const newMotCible = currentPoolCible[cibleIndex];
    const newPoolCbl = [...currentPoolCible.slice(0, cibleIndex), ...currentPoolCible.slice(cibleIndex + 1)];

    // Ajouter aux listes visibles
    const newSource = [...currentSource, newMot];
    // Insérer le mot cible à une position aléatoire
    const insertPos = Math.floor(Math.random() * (currentCible.length + 1));
    const newCible = [...currentCible.slice(0, insertPos), newMotCible, ...currentCible.slice(insertPos)];

    return { newSource, newCible, newPoolSource: newPoolSrc, newPoolCible: newPoolCbl };
  };

  const handleSelection = (mot, isSource) => {
    if (isSource) {
      if (selectedSource?.id === mot.id) {
        setSelectedSource(null);
        return;
      }

      if (selectedTarget) {
        const isCorrectPair = mot.pairId === selectedTarget.pairId;
        console.log(`Vérification: ${mot.mot} (pairId=${mot.pairId}) ↔ ${selectedTarget.mot} (pairId=${selectedTarget.pairId}) → ${isCorrectPair}`);

        if (isCorrectPair) {
          const newScore = score + 1;
          const newResolved = resolved + 1;
          setScore(newScore);
          setResolved(newResolved);

          const filteredSource = motsSource.filter((m) => m.id !== mot.id);
          const filteredCible = motsCible.filter((t) => t.id !== selectedTarget.id);

          // Compléter avec une paire du pool
          const { newSource, newCible, newPoolSource, newPoolCible } = pullFromPool(filteredSource, filteredCible, poolSource, poolCible);
          setMotsSource(newSource);
          setMotsCible(newCible);
          setPoolSource(newPoolSource);
          setPoolCible(newPoolCible);
        } else {
          setScore(score - 2);
        }
        setSelectedSource(null);
        setSelectedTarget(null);
      } else {
        setSelectedSource(mot);
      }
    } else {
      if (selectedTarget?.id === mot.id) {
        setSelectedTarget(null);
        return;
      }

      if (selectedSource) {
        const isCorrectPair = selectedSource.pairId === mot.pairId;
        console.log(`Vérification: ${selectedSource.mot} (pairId=${selectedSource.pairId}) ↔ ${mot.mot} (pairId=${mot.pairId}) → ${isCorrectPair}`);

        if (isCorrectPair) {
          const newScore = score + 1;
          const newResolved = resolved + 1;
          setScore(newScore);
          setResolved(newResolved);

          const filteredSource = motsSource.filter((m) => m.id !== selectedSource.id);
          const filteredCible = motsCible.filter((t) => t.id !== mot.id);

          // Compléter avec une paire du pool
          const { newSource, newCible, newPoolSource, newPoolCible } = pullFromPool(filteredSource, filteredCible, poolSource, poolCible);
          setMotsSource(newSource);
          setMotsCible(newCible);
          setPoolSource(newPoolSource);
          setPoolCible(newPoolCible);
        } else {
          setScore(score - 2);
        }
        setSelectedSource(null);
        setSelectedTarget(null);
      } else {
        setSelectedTarget(mot);
      }
    }
  };

  const resetExercise = () => {
    setStep(1);
    setScore(0);
    setResolved(0);
    setSelectedSource(null);
    setSelectedTarget(null);
    setPoolSource([]);
    setPoolCible([]);
  };

  const getArticle = (langue, genre, mot, type) => {
    if (type !== 'nom' || !genre) return '';
    const article = getArticleDefini(langue, genre, 'singulier', mot);
    if (!article) return '';
    return article.endsWith("'") ? article : article + ' ';
  };

  if (step === 1) {
    return (
      <div className="matching-exercise param-selection">
        {!isEmbed && (
          <div className="exercise-header">
            <div className="header-title">
              <h2>{t('matching.title')}</h2>
            </div>
          </div>
        )}
        <p>{t('matching.instructions')}</p>

        <div className="param-group">
          <label>{t('matching.langueSource')}</label>
          <select value={params.langueSource} onChange={(e) => {
              const newSource = e.target.value;
              const newCible = params.langueCible === newSource
                ? langues.find((l) => l.code !== newSource)?.code || ''
                : params.langueCible;
              setParams({ ...params, langueSource: newSource, langueCible: newCible });
            }} className="language-select">
            {langues.map((langue) => (
              <option key={langue.code} value={langue.code}>
                {langue.flag} {langue.name}
              </option>
            ))}
          </select>
        </div>

        <div className="param-group">
          <label>{t('matching.langueCible')}</label>
          <select value={params.langueCible} onChange={(e) => setParams({ ...params, langueCible: e.target.value })} className="language-select">
            {langues
              .filter((l) => l.code !== params.langueSource)
              .map((langue) => (
                <option key={langue.code} value={langue.code}>
                  {langue.flag} {langue.name}
                </option>
              ))}
          </select>
        </div>

        <div className="param-group">
          <label>{t('matching.typeMot')}</label>
          <select value={params.typeMot} onChange={(e) => setParams({ ...params, typeMot: e.target.value })} className="type-select">
            {typesMots.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="param-group">
          <label>{t('matching.nombreQuestions')}</label>
          <select value={params.nombreQuestions} onChange={(e) => setParams({ ...params, nombreQuestions: parseInt(e.target.value) })} className="quantity-select">
            {[10, 20, 50].map((q) => (
              <option key={q} value={q}>
                {t('matching.questionsCount', { count: q })}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="error-message">❌ {error}</div>}

        <button onClick={startExercise} disabled={loading} className="start-button">
          {loading ? t('matching.loading') : t('matching.start')}
        </button>
      </div>
    );
  }

  return (
    <div className="matching-exercise exercise-card">
      <div className="header">
        {!isEmbed && <h2>{t('matching.title')}</h2>}
        <div className="score-row">
          <span className="header-flags">
            {langues.find((l) => l.code === params.langueSource)?.flag} → {langues.find((l) => l.code === params.langueCible)?.flag}
          </span>
          <span className="progress-counter">{resolved}/{totalQuestions}</span>
          <div className="score">{t('matching.score')}: {score}</div>
          <button onClick={resetExercise} className="reset-button">
            {t('matching.restart')}
          </button>
        </div>
      </div>

      <div className="columns-container">
        <div className="source-column">
          {motsSource.map((mot) => {
            const article = getArticle(mot.langue, mot.genre, mot.mot, mot.type);
            return (
              <div key={mot.id} className={`word-card ${selectedSource?.id === mot.id ? 'selected' : ''}`} onClick={() => handleSelection(mot, true)}>
                <span className="word-main">
                  {article && <span className="article">{article}</span>}
                  <span className="word">{mot.mot}</span>
                  {mot.contexte && <span className="context"> {mot.contexte}</span>}
                </span>
                <span className="type">({mot.type})</span>
              </div>
            );
          })}
        </div>

        <div className="target-column">
          {motsCible.map((mot) => {
            const article = getArticle(mot.langue, mot.genre, mot.mot, mot.type);
            return (
              <div key={mot.id} className={`word-card ${selectedTarget?.id === mot.id ? 'selected' : ''}`} onClick={() => handleSelection(mot, false)}>
                <span className="word-main">
                  {article && <span className="article">{article}</span>}
                  <span className="word">{mot.mot}</span>
                  {mot.contexte && <span className="context"> {mot.contexte}</span>}
                </span>
                <span className="type">({mot.type})</span>
              </div>
            );
          })}
        </div>
      </div>

      {motsSource.length === 0 && (
        <div className="completion-message">
          {t('matching.completion', { score })}
          <button onClick={resetExercise} className="restart-button">
            {t('matching.restartBtn')}
          </button>
        </div>
      )}
    </div>
  );
}

export default MatchingExercise;
